import "./EditAd.scss";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import bin from "../../assets/images/icons/delete.svg";
import checkMark from "../../assets/images/icons/check-mark.svg";
import chevronLeft from "../../assets/images/icons/chevron-left.svg";
import Image from "../../components/Image";
import InputForm from "../../components/InputForm/InputForm";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import * as Yup from "yup";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SelectForm from "../../components/SelectForm/SelectForm";
import AvailabilityCalendar from "../../components/AvailabilityCalendar";
import Button from "../../components/Buttons/Button";

const EditAd = () => {
    const navigate = useNavigate();
    const { adID } = useParams();
    const [ad, setAd] = useState({});
    const [adToDelete, setAdToDelete] = useState({});
    const [businessName, setBusinessName] = useState();
    const [location, setLocation] = useState();
    const [website, setWebsite] = useState();
    // yyyy-MM-dd strings, same as the add form. dateEnd is EXCLUSIVE: the
    // server serves while date_end > now, so the ad's last live day is the one
    // before it.
    const [dateStart, setDateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");
    const [availability, setAvailability] = useState({});
    const [maxPerDay, setMaxPerDay] = useState(1);
    const [about, setAbout] = useState();
    const [type, setType] = useState();

    const [errors, setErrors] = useState({});

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const getAd = async () => {
            try {
                const response = await api.get(`/ads/${adID}`);


                setAd(response.data);
            } catch (err) {
                console.log(`unable to get single ad. this was the error: ${err}`);
            }
        };
        getAd();
    }, [adID]);

    // Stored dates are ms at Miami midnight, which lands on the same calendar
    // day in UTC — the same key the server builds its availability map from.
    const ymdFromMs = (ms) => (ms ? new Date(ms).toISOString().slice(0, 10) : "");

    const shiftYmd = (ymd, days) => {
        const [y, m, d] = ymd.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + days);
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    };

    const prettyDay = (ymd) => {
        if (!ymd) return "";
        const [y, m, d] = ymd.split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    };

    function handleDeleteAd(id) {
        const deleteAd = async () => {
            try {
                await api.delete(`/ads/${adToDelete}`);
                navigate("/ad-management");
            } catch (err) {
                console.log(`failed to delete ad. this is the error: ${err}`);
            }
        };

        deleteAd();
    }

    const validationSchema = Yup.object().shape({
        // businessName: Yup.string().required("Business name is required"),
        // location: Yup.string().required("Location is required"),
        // website: Yup.string().required("Website is required"),
        // dateStart: Yup.string().required("Date Start is required"),
        // dateEnd: Yup.string().required("Date End is required"),
        // about: Yup.string().required("About is required"),
    });

    const handleAddingAd = async (event) => {
        event.preventDefault();

        if (dateStart && !dateEnd) {
            toast.error("Tap the last day on the calendar to finish the date range.");
            return;
        }

        try {
            await validationSchema.validate(
                {
                    // businessName,
                    // location,
                    // website,
                    // dateStart,
                    // dateEnd,
                    // about,
                },
                { abortEarly: false }
            );

            const newAd = {
                business_name: businessName,
                location: selectedVenues?.value,
                event: selectedEvents?.value,
                website: website,
                date_start: dateStart,
                date_end: dateEnd,
                about: about,
                type: type
            };

            console.log(newAd);

            await api.put(`/ads/${adID}`, newAd);

            toast.success('Ad Edit successfully.');

            navigate("/ad-management");
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const validationErrors = {};
                error.inner.forEach((err) => {
                    validationErrors[err.path] = err.message;
                });
                setErrors(validationErrors);
                toast.error(`Error Edit Ad. Please try again, ${error.message}`);

            } else {
                console.error("Error adding user:", error);
            }
        }
    };

    useEffect(() => {
        setBusinessName(ad.business_name);
        setLocation(ad.website);
        setWebsite(ad.website);
        setImages(ad.photo_url);
        setAbout(ad.about);
        setType(ad.type)
        setDateStart(ymdFromMs(ad.date_start))
        setDateEnd(ymdFromMs(ad.date_end))
    }, [ad]);

    // Same availability source as the add form and the ads portal calendar.
    useEffect(() => {
        api
            .get("/ads/booked-dates")
            .then((res) => {
                setAvailability(res.data?.availability || {});
                setMaxPerDay(res.data?.max_per_day || 1);
            })
            .catch(() => {});
    }, []);

    // This ad's own days are inside those counts, so discount them — otherwise
    // an ad reads as blocking itself and its dates can't be re-picked. Mirror
    // the server's own inclusion rule: a live-or-future booking, paid or held.
    const bookedDates = useMemo(() => {
        const own = new Set();
        const ownStart = ymdFromMs(ad.date_start);
        const ownEnd = ymdFromMs(ad.date_end);
        const counted =
            ad.date_end >= Date.now() &&
            (ad.paid || (ad.hold_expires_at || 0) > Date.now());

        if (counted && ownStart && ownEnd) {
            for (let d = ownStart; d < ownEnd; d = shiftYmd(d, 1)) own.add(d);
        }

        return Object.entries(availability)
            .filter(([day, count]) => count - (own.has(day) ? 1 : 0) >= maxPerDay)
            .map(([day]) => day);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availability, maxPerDay, ad]);

    const lastLiveDay = dateEnd ? shiftYmd(dateEnd, -1) : "";

    const handleDelete = async () => {
        try {
            await api.delete(`/ads/${adID}`);
            toast.success('Ad Delete successfully.');

            navigate("/ad-management");
        } catch (err) {
            console.log(`failed to delete ads. this is the error: ${err}`);
        }
    };

    const [images, setImages] = useState(null);

    const handleImageAdd = useCallback(
        async (events) => {
            if (!ad?._id) return;

            const file = events.target.files[0];

            const formData = new FormData();

            formData.append("photo", file);

            const { data } = await api.post(`/ads/photo/${ad._id}`, formData);

            // const recentImageAdded = data.photo;

            // const image = { id: recentImageAdded };

            setImages(data.photo);

            alert("Image uploaded successfully.");
        },
        [ad?._id]
    );

    const handleImageRemove = async () => {
        if (!ad?._id) return;

        await api.delete(`/ads/photo/${ad._id}`);

        setImages(null);

        alert("Image DELETED successfully.");
    };


    const [loadingVenuesAndEvents, setLoadingVenuesAndEvents] = useState(true);
    const [listVenues, setListVenues] = useState([]);
    const [listEvents, setListEvents] = useState([]);
    const [selectedVenues, setSelectedVenues] = useState(null);
    const [selectedEvents, setSelectedEvents] = useState(null);

    useEffect(() => {
        setLoadingVenuesAndEvents(true);

        const fetchData = async () => {
            try {
                const [venuesResponse, anotherResponse] = await Promise.all([
                    api.get("/venues", {
                        params: {
                            records: 99999999,
                            page: 1,
                        },
                    }),
                    api.get("/events", {
                        params: {
                            records: 99999999,
                            page: 1,
                            // Keep an ad's event in the list even if its venue was hidden since,
                            // or saving would drop the event from the ad.
                            includeInactive: true,
                        },
                    }),
                ]);

                setListVenues(venuesResponse.data.data);
                setListEvents(anotherResponse.data.data);

                setLoadingVenuesAndEvents(false);
            } catch (error) {
                console.log(`API requests didn't work. Error: ${error}`);
                setLoadingVenuesAndEvents(false);
            }
        };

        fetchData();
    }, []);

    const optionsVenus = listVenues.map((venue) => ({
        value: venue._id,
        label: venue.name,
    }));

    const optionsEvents = listEvents.map((venue) => ({
        value: venue._id,
        label: venue.name,
    }));

    const handleCategoryVenues = (selectedOptions) => {
        setSelectedVenues(selectedOptions);
    };

    const handleCategoryEvents = (selectedOptions) => {
        setSelectedEvents(selectedOptions);
    };


    const venueId = optionsVenus.find(
        (option) => option.value === ad.location
    );
    const eventsId = optionsEvents.find(
        (option) => option.value === ad.event
    );

    useEffect(() => {
        if (venueId && !selectedVenues) {
            setSelectedVenues(venueId);
        }
        if (eventsId && !selectedEvents) {
            setSelectedEvents(eventsId);
        }
    }, [venueId, selectedVenues, selectedEvents, eventsId]);


    return (
        <main>
            <TopNav />
            <div className="main-container">
                <SideBar />
                <article className="page-content">
                    <section className="add-ad">
                        <div className="container-header">
                            <div className="add-ad__top-left">
                                <img
                                    src={chevronLeft}
                                    className="add-ad__top-left-icon"
                                    onClick={() => navigate(-1)}
                                    alt=""
                                ></img>
                                <h1 className="add-ad__top-left-heading">Edit ad</h1>
                            </div>

                            <Button
                                className="btn-outline-light"
                                onClick={handleDelete}
                                label={'Delete Ad'}
                                iconifyIconName="mynaui:trash"
                            />
                        </div>

                        <form onSubmit={handleAddingAd} className="add-ad__form">
                            <section>
                                <div>
                                    <div className="fade-up">
                                        <InputForm
                                            placeholder="Enter Business Name"
                                            label="Business Name"
                                            type="text"
                                            id="businessName"
                                            name="businessName"
                                            value={businessName}
                                            onChange={(event) => setBusinessName(event.target.value)}
                                            error={errors.businessName}
                                        />
                                    </div>

                                    <div className="grid gap-4 fade-up">
                                        <SelectForm
                                            isLoading={loadingVenuesAndEvents}
                                            onChange={handleCategoryVenues}
                                            options={optionsVenus}
                                            value={selectedVenues}
                                            error={errors.selectedVenues}
                                            label="Venue"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="grid gap-4 fade-up">
                                        <SelectForm
                                            isLoading={loadingVenuesAndEvents}
                                            onChange={handleCategoryEvents}
                                            options={optionsEvents}
                                            value={selectedEvents}
                                            error={errors.selectedEvents}
                                            label="Events"
                                        />
                                    </div>
                                    <div className="fade-up">
                                        <InputForm
                                            placeholder="Enter Website"
                                            label="Website"
                                            type="text"
                                            id="website"
                                            name="website"
                                            value={website}
                                            onChange={(event) => setWebsite(event.target.value)}
                                            error={errors.website}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <label style={{ padding: "0.5vw 2vw", color: "#fff" }}>
                                            Dates
                                        </label>
                                        <AvailabilityCalendar
                                            bookedDates={bookedDates}
                                            start={dateStart}
                                            endExclusive={dateEnd}
                                            minDay={ymdFromMs(ad.date_start)}
                                            onChange={(nextStart, nextEnd) => {
                                                setDateStart(nextStart);
                                                setDateEnd(nextEnd);
                                            }}
                                        />
                                        <span
                                            style={{
                                                display: "block",
                                                padding: "8px 2vw 0",
                                                fontSize: 13,
                                                color: "#888",
                                            }}
                                        >
                                            {dateStart && lastLiveDay
                                                ? `Runs ${prettyDay(dateStart)} through ${prettyDay(lastLiveDay)}, last day included.`
                                                : ""}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <Image
                                        onRemove={handleImageRemove}
                                        defaultValue={images}
                                        onAdd={handleImageAdd}
                                    />
                                </div>

                                <div>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <label style={{ color: "#fff", padding: "0.5vw 2vw" }}>
                                            About
                                        </label>
                                        <textarea
                                            rows={4}
                                            id="venueDescription"
                                            name="about"
                                            value={about}
                                            onChange={(event) => setAbout(event.target.value)}
                                            placeholder="Enter about"
                                            className="venue-info__form-group-about-input"
                                            maxLength={108}
                                        />
                                        <span style={{ display: 'block', textAlign: 'right', fontSize: 12, color: '#888', marginTop: 4 }}>{(about || '').length}/108</span>
                                    </div>
                                </div>

                                <span>
                                    <Button
                                        primaryBtn
                                        type="submit"
                                        label={'Save Information'}
                                        disabled={loading}
                                        iconifyIconName="material-symbols:check-rounded"
                                    />
                                </span>
                            </section>
                        </form>
                    </section>
                </article>
            </div>
        </main>
    );
}

export default EditAd;
